import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Hash, Boxes, FileText, Copy, Check } from "lucide-react";

export default function RawMaterialDetailModal({ material, onClose }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleCopy = () => {
    navigator.clipboard?.writeText(material.cas_number || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const infoIconStyle = {
    width: "34px",
    height: "34px",
    minWidth: "34px",
    background: "rgba(12,104,57,0.1)",
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0c6839",
    flexShrink: "0",
  };

  const infoRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    background: "#f8faf8",
    borderRadius: "12px",
    padding: "13px 16px",
    boxSizing: "border-box",
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: "0",
        background: "rgba(0,0,0,0.45)",
        zIndex: "1000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <div
        dir={isRtl ? "rtl" : "ltr"}
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          width: "100%",
          maxWidth: "520px",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
          textAlign: isRtl ? "right" : "left",
        }}
      >
        <button
          onClick={onClose}
          aria-label={t("marketplace.close") || "Close"}
          style={{
            position: "absolute",
            top: "14px",
            [isRtl ? "left" : "right"]: "14px", 
            zIndex: "10",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            border: "1px solid #e5e7eb",
            background: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#6b7280",
          }}
        >
          <X size={16} />
        </button>

        <div
          style={{
            height: "180px",
            background: "rgba(12,104,57,0.06)",
            borderRadius: "24px 24px 0 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {material.image ? (
            <img
              src={material.image}
              alt={material.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <svg
              width="56"
              height="56"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(12,104,57,0.3)"
              strokeWidth="1.2"
            >
              <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
            </svg>
          )}
        </div>

        <div style={{ padding: "28px", boxSizing: "border-box" }}>
          <h2
            style={{
              fontSize: "22px",
              fontWeight: "700",
              color: "#1a1f1c",
              lineHeight: "1.3",
              marginBottom: "10px",
              marginTop: "0",
            }}
          >
            {material.name}
          </h2>

          <span
            style={{
              display: "inline-block",
              fontSize: "12px",
              fontWeight: "600",
              color: "#0c6839",
              background: "rgba(12,104,57,0.1)",
              border: "1px solid rgba(12,104,57,0.2)",
              padding: "5px 14px",
              borderRadius: "20px",
              marginBottom: "22px",
            }}
          >
            {material.family?.name || t("products.chemicals")}
          </span>

          <div style={{ marginBottom: "20px" }}>
            <p
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "14px",
                fontWeight: "700",
                color: "#1a1f1c",
                marginBottom: "8px",
                marginTop: "0",
              }}
            >
              <FileText size={15} color="#0c6839" />
              {t("products.description")}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                lineHeight: "1.7",
                margin: "0",
              }}
            >
              {material.description || t("products.noDescription")}
            </p>
          </div>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <div style={infoRowStyle}>
              <span style={infoIconStyle}>
                <Hash size={16} />
              </span>
              <div
                style={{
                  flex: "1",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                }}
              >
                <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                  {t("products.casNumber")}
                </span>
                <span
                  style={{
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#1a1f1c",
                  }}
                >
                  {material.cas_number || "N/A"}
                </span>
              </div>
              <button
                onClick={handleCopy}
                aria-label="Copy CAS Number"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: copied ? "#0c6839" : "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                  padding: "4px",
                  borderRadius: "6px",
                }}
              >
                {copied ? <Check size={15} /> : <Copy size={15} />}
              </button>
            </div>

            {material.categories?.length > 0 && (
              <div style={infoRowStyle}>
                <span style={infoIconStyle}>
                  <Boxes size={16} />
                </span>
                <div
                  style={{
                    flex: "1",
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                    {t("products.applications")}
                  </span>
                  <span
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#1a1f1c",
                    }}
                  >
                    {material.categories.map((c) => c.name).join(" - ")}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
